/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-03-03 15:06:11                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-03 15:10:07                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * TechnicianPerfDownloader: Component to download technicians performances
*/

/* Imports */
import { useState } from "react";
import { Button } from "@material-tailwind/react";
import { format, parse, setWeek } from "date-fns";
import { api } from "../../../services/api/api";
import PeriodSelect, { PeriodSelectData } from "../date/PeriodSelect";
import * as XLSX from "xlsx";
/***/

interface TechnicianPerfDownloaderProps {
  className?: string;
}

interface TechnicianPerf {
  technician: {
    firstName?: string;
    lastName?: string;
    email?: string | null;
  };
  prospects?: {
    count?: number;
    chronoDays?: number;
  };
  workOrders?: {
    count?: number;
    chronoDays?: number;
    offDuty?: number;
    afterProspect?: number;
  };
  leaveDays?: number;
  details?: Array<Omit<DetailItem, "technician" | "technicianEmail">>;
}

// Interface for detail item
interface DetailItem {
  technician?: string;
  technicianEmail?: string;
  customer: string;
  activitySector: string;
  prospectRef: string | null;
  prospectDate: number | null;
  workOrderRef: string | null;
  workOrderStartDate: number | null;
  workOrderEndDate: number | null;
  onCallDuty?: boolean | null;
  isOnCallDuty?: boolean | null;
  chronoDays: number;
  prospectNotes: string | null;
  workOrderNotes: string | null;
}

const AFTER_PROSPECT_BONUS_POINTS = 1;
const WORK_ORDER_WITH_PROSPECT_POINTS_WITHOUT_BONUS = 5;
const ON_CALL_DUTY_POINTS = 1;

const extractPerfs = (response: unknown): TechnicianPerf[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (response && typeof response === "object") {
    const data = (response as { data?: unknown }).data;

    if (Array.isArray(data)) {
      return data;
    }
  }

  return [];
};

const getTechnicianName = (perf: TechnicianPerf) => {
  return `${perf.technician.firstName || ""} ${perf.technician.lastName || ""}`.trim();
};

const hasWorkOrderAndProspect = (detail: Pick<DetailItem, "prospectRef" | "workOrderRef">) => {
  return Boolean(detail.prospectRef && detail.workOrderRef);
};

const isDetailOnCallDuty = (detail: Pick<DetailItem, "onCallDuty" | "isOnCallDuty">) => {
  return detail.onCallDuty ?? detail.isOnCallDuty ?? false;
};

/**
 * Returns the after-prospect bonus that is missing from a detail row.
 *
 * The backend is the source of truth for chrono days, but older/stale rows can
 * still contain the pre-bonus value. To keep the export idempotent, the bonus is
 * added only when the row exactly matches the known "without bonus" totals:
 * 5 points for visit + intervention, or 6 when the intervention was on call.
 */
const getMissingAfterProspectBonus = (
  detail: Pick<DetailItem, "prospectRef" | "workOrderRef" | "onCallDuty" | "isOnCallDuty" | "chronoDays">
) => {
  if (!hasWorkOrderAndProspect(detail) || typeof detail.chronoDays !== "number") {
    return 0;
  }

  const expectedChronoDaysWithoutBonus = WORK_ORDER_WITH_PROSPECT_POINTS_WITHOUT_BONUS
    + (isDetailOnCallDuty(detail) ? ON_CALL_DUTY_POINTS : 0);

  return detail.chronoDays === expectedChronoDaysWithoutBonus ? AFTER_PROSPECT_BONUS_POINTS : 0;
};

const getExportChronoDays = (
  detail: Pick<DetailItem, "prospectRef" | "workOrderRef" | "onCallDuty" | "isOnCallDuty" | "chronoDays">
) => {
  const chronoDays = typeof detail.chronoDays === "number" ? detail.chronoDays : 0;

  return chronoDays + getMissingAfterProspectBonus(detail);
};

const getExportAfterProspectCount = (perf: TechnicianPerf) => {
  if (perf.details && Array.isArray(perf.details)) {
    return perf.details.filter(hasWorkOrderAndProspect).length;
  }

  return perf.workOrders?.afterProspect || 0;
};

const getExportTotalChronoDays = (perf: TechnicianPerf) => {
  if (perf.details && Array.isArray(perf.details) && perf.details.length > 0) {
    return perf.details.reduce((total, detail) => {
      return total + getExportChronoDays(detail);
    }, 0);
  }

  const totalChronoDays = (perf.workOrders?.chronoDays || 0) + (perf.prospects?.chronoDays || 0);
  return totalChronoDays;
};

const getFallbackOnCallDuty = (perf: TechnicianPerf) => {
  return perf.workOrders?.offDuty ? "Y" : "N";
};

/**
* TechnicianPerfDownloader: Component to download technicians performances
* @param className - Additional CSS classes
*/
export default function TechnicianPerfDownloader({ className = "" }: TechnicianPerfDownloaderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [periodData, setPeriodData] = useState<PeriodSelectData>(() => {
    return {
      period: "day",
      value: format(new Date(), "yyyy-MM-dd")
    };
  });

  const getDateRange = () => {
    const today = new Date();
    let from: Date;

    switch (periodData.period) {
      case "week": {
        const [year, weekNumberStr] = periodData.value.split("-W");
        const week = parseInt(weekNumberStr);
        from = setWeek(new Date(parseInt(year), 0, 4), week, { weekStartsOn: 1 });
        const to = new Date(from);
        to.setDate(from.getDate() + 6);
        today.setTime(to.getTime());
        break;
      }
      case "month": {
        const [year, month] = periodData.value.split("-");
        from = new Date(parseInt(year), parseInt(month) - 1, 1);
        const lastDayOfMonth = new Date(parseInt(year), parseInt(month), 0);
        today.setTime(lastDayOfMonth.getTime());
        break;
      }
      default: {
        from = parse(periodData.value, "yyyy-MM-dd", new Date());
        today.setTime(from.getTime());
      }
    }

    return {
      from: format(from, "yyyy-MM-dd"),
      to: format(today, "yyyy-MM-dd")
    };
  };

  const downloadPerfs = async () => {
    setIsLoading(true);
    try {
      const { from, to } = getDateRange();

      const response = await api.get(`admin/perf?from=${from}&to=${to}`);
      const perfs = extractPerfs(response);

      // Create data for Stats worksheet with new columns
      const statsHeaders = [
        "Tech name",
        "Tech mail",
        "Nb. Prospect",
        "Nb. Intervention",
        "Nb. Wo/Pro",
        "On Call Duty",
        "Chrono Days",
        "Rest Days"
      ];

      // Create data for Stats worksheet
      const statsData = [
        statsHeaders,
        ...perfs.map(perf => {
          const prospectCount = perf.prospects?.count || 0;
          const workOrderCount = perf.workOrders?.count || 0;
          const afterProspectCount = getExportAfterProspectCount(perf);
          const onCallDutyPoints = perf.workOrders?.offDuty || 0;
          const totalChronoDays = getExportTotalChronoDays(perf);

          return [
            getTechnicianName(perf),
            perf.technician.email || "",
            prospectCount,
            workOrderCount,
            afterProspectCount,
            onCallDutyPoints,
            totalChronoDays,
            perf.leaveDays || 0
          ];
        })
      ];

      // Create details data for List worksheet
      const detailsHeaders = [
        "Tech name",
        "Tech mail",
        "Customer",
        "Activity Sector",
        "Prospect Ref",
        "Prospect Date",
        "Intervention Ref",
        "Intervention Start",
        "Intervention End",
        "On Call Duty",
        "Chrono Days",
        "Prospect remarks",
        "Intervention notes"
      ];

      // Collect all details from all technicians
      const allDetails: DetailItem[] = [];
      perfs.forEach(perf => {
        if (perf.details && Array.isArray(perf.details)) {
          // Add technician info to each detail
          const technicianDetails = perf.details.map((detail: Omit<DetailItem, "technician" | "technicianEmail">) => ({
            ...detail,
            technician: getTechnicianName(perf),
            technicianEmail: perf.technician.email || ""
          }));
          allDetails.push(...technicianDetails);
        }
      });

      // Format dates and create detail rows
      const detailRows = allDetails.length > 0 ? allDetails.map((detail: DetailItem) => {
        const isOnCallDuty = detail.onCallDuty ?? detail.isOnCallDuty ?? false;
        // Format dates if they exist
        const prospectDate = detail.prospectDate
          ? format(new Date(detail.prospectDate), "yyyy-MM-dd")
          : "";
        const workOrderStartDate = detail.workOrderStartDate
          ? format(new Date(detail.workOrderStartDate), "yyyy-MM-dd HH:mm")
          : "";
        const workOrderEndDate = detail.workOrderEndDate
          ? format(new Date(detail.workOrderEndDate), "yyyy-MM-dd HH:mm")
          : "";

        return [
          detail.technician || "",
          detail.technicianEmail || "",
          detail.customer || "",
          detail.activitySector || "",
          detail.prospectRef || "",
          prospectDate,
          detail.workOrderRef || "",
          workOrderStartDate,
          workOrderEndDate,
          detail.workOrderRef ? (isOnCallDuty ? "Y" : "N") : "",
          getExportChronoDays(detail),
          detail.prospectNotes || "",
          detail.workOrderNotes || ""
        ];
      }) : perfs.map(perf => [
        getTechnicianName(perf),
        perf.technician.email || "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        getFallbackOnCallDuty(perf),
        getExportTotalChronoDays(perf),
        "",
        ""
      ]);

      // Create data for second worksheet (Details)
      const detailsData = [
        detailsHeaders,
        ...detailRows
      ];

      // Create workbook with two worksheets
      const workbook = XLSX.utils.book_new();

      // Add details worksheet first with the expected detailed export columns.
      const detailsWorksheet = XLSX.utils.aoa_to_sheet(detailsData);
      XLSX.utils.book_append_sheet(workbook, detailsWorksheet, "Lists");

      // Add stats worksheet second for aggregated technician counters.
      const statsWorksheet = XLSX.utils.aoa_to_sheet(statsData);
      XLSX.utils.book_append_sheet(workbook, statsWorksheet, "Stats");

      // Generate Excel file
      XLSX.writeFile(workbook, `performances_${from}_${to}.xlsx`);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex flex-col h-full p-4 ${className}`}>
      <div className="flex-grow pb-4">
        <PeriodSelect
          onChange={setPeriodData}
          initialData={{
            period: "day",
            value: format(new Date(), "yyyy-MM-dd")
          }}
        />
      </div>

      <div className="footer mt-auto pt-4">
        <Button
          onClick={downloadPerfs}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-blue-500"
          color="blue"
          size="sm"
        >
          {isLoading ? "Downloading..." : "Download performances"}
        </Button>
      </div>
    </div>
  );
} 
